/**
 * StageTrailSection — a sequência de follow-up DESTA etapa.
 *
 * Mora dentro das automações da etiqueta, logo depois do auto-disparo, porque
 * é a continuação natural dele: o auto-disparo manda uma mensagem na hora que
 * o contato entra; a sequência segue mandando depois, em degraus de tempo, e
 * para sozinha quando o contato responde.
 *
 * Por que aqui e não no botão "Régua" do funil: a régua é uma regra
 * TRANSVERSAL ("o cliente parou de responder", podendo vigiar várias etapas de
 * uma vez). A sequência é por etapa, com conteúdo próprio de cada etapa. Eram
 * duas ideias diferentes na mesma tela, e o cliente tinha que entender a
 * palavra "âncora" para escolher.
 *
 * Backend: /api/reply-rulers (uma régua com anchor=stage_entry e label_ids
 * contendo só esta etiqueta). O histórico vem de /{id}/history já em português.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Timer, Plus, Trash2, Loader2, AlertCircle, Check, Paperclip,
  ListOrdered, ShieldCheck, History, ChevronDown, ChevronUp,
} from 'lucide-react';
import replyRulerService from '../../services/global/replyRulerService';
import fileUploadService from '../../services/global/fileUploadService';
import templateService from '../../services/global/templateService';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import TemplateMessageField from '../messages/TemplateMessageField';
import BusinessWindowFields from '../common/BusinessWindowFields';
import TemplatePicker from '../messages/TemplatePicker';
import {
  analyzeTemplate, findTemplate, uniqueApproved, templateCapabilities, templateUsability,
  missingValues, valuesFromMessage,
} from '../../utils/templateParams';
import { channelOptions, isWindowChannel, supports, whereSupported } from '../../utils/channels';

const MAX_STEP_FILES = 3;
const MAX_STEPS = 10;

const ACTIONS = [
  { value: 'message', label: 'Mensagem de texto' },
  { value: 'media', label: 'Arquivo (vídeo, PDF, imagem)' },
  { value: 'agent', label: 'Deixar a IA escrever' },
  { value: 'template', label: 'Template aprovado (Business)' },
];

// Opcoes de canal pelo mapa de capacidades (utils/channels.js); o QR
// primeiro, como esta tela sempre mostrou.
const CHANNELS = channelOptions('distinct', ['wa_personal', 'wa_business', 'instagram', 'messenger']);

// after_minutes <-> (valor, unidade), para o cliente pensar em horas e dias.
const toDisplay = (minutes) => {
  const m = Number(minutes) || 0;
  if (m >= 1440 && m % 1440 === 0) return { value: m / 1440, unit: 'days' };
  if (m >= 60 && m % 60 === 0) return { value: m / 60, unit: 'hours' };
  return { value: m, unit: 'minutes' };
};
const toMinutes = (value, unit) => {
  const v = Math.max(1, Number(value) || 1);
  if (unit === 'days') return v * 1440;
  if (unit === 'hours') return v * 60;
  return v;
};

const emptyStep = (afterMinutes) => ({
  after_minutes: afterMinutes,
  action: 'message',
  text: '',
  instruction: '',
  template_name: '',
  template_language: 'pt_BR',
  files: [],
  close: false,
  move_to_label_id: '',
});

const DEFAULT_GUARDS = {
  min_silence_minutes: 60,
  min_interval_minutes: 120,
  max_delay_multiplier: 5,
  max_attempts: 3,
  quiet_hours: {
    enabled: false, start: '20:00', end: '08:00',
    business_hours_only: false, weekdays: [],
  },
};

const StageTrailSection = ({
  label, pipelineId, funnelLabels, colors, stepStyles, autoDispatchEnabled,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  const [available, setAvailable] = useState(false);

  const [rulerId, setRulerId] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [channel, setChannel] = useState('wa_personal');
  const [steps, setSteps] = useState([]);
  const [guards, setGuards] = useState(DEFAULT_GUARDS);
  const [validateWithAgent, setValidateWithAgent] = useState(false);
  const [fallbackTemplate, setFallbackTemplate] = useState('');

  const [uploadingStep, setUploadingStep] = useState(null);
  const [showGuards, setShowGuards] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [fallbackLanguage, setFallbackLanguage] = useState('pt_BR');
  // Seletor único e editor de variáveis (2026-09-14). Sem a flag, como antes.
  const { flags } = useFeatureFlags();
  const templateUi = Boolean(flags.template_params_ui);
  const trailCaps = templateCapabilities('trail', flags);
  const fallbackCaps = templateCapabilities('fallback', flags);

  const labelId = label?.id;

  // Carrega a sequência DESTA etapa.
  useEffect(() => {
    if (!labelId) return;
    let cancelled = false;
    setLoading(true);
    replyRulerService.list()
      .then((res) => {
        if (cancelled) return;
        setAvailable(!!res?.trails_enabled);
        const mine = (res?.data || []).find(
          (r) => r.anchor === 'stage_entry' && (r.label_ids || []).includes(labelId)
        );
        if (mine) {
          setRulerId(mine.id);
          setEnabled(!!mine.enabled);
          setChannel(mine.channel || 'wa_personal');
          setSteps((mine.steps || []).map((s) => ({
            ...emptyStep(s.after_minutes), ...s, files: s.files || [],
          })));
          setGuards({ ...DEFAULT_GUARDS, ...(mine.guards || {}),
            quiet_hours: { ...DEFAULT_GUARDS.quiet_hours, ...((mine.guards || {}).quiet_hours || {}) } });
          setValidateWithAgent(!!mine.validate_with_agent);
          setFallbackTemplate(mine.fallback_template_name || '');
          setFallbackLanguage(mine.fallback_template_language || 'pt_BR');
        } else {
          setRulerId(null);
          setEnabled(false);
          setSteps([]);
        }
      })
      .catch(() => { if (!cancelled) setAvailable(false); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [labelId]);

  // Templates aprovados: só quando o canal é Business e alguém precisa deles.
  const needsTemplates = channel === 'wa_business';
  useEffect(() => {
    if (!needsTemplates) return;
    let cancelled = false;
    templateService.list()
      .then((d) => {
        if (cancelled) return;
        setTemplates((d?.templates || []).filter(
          (t) => (t.status || '').toUpperCase() === 'APPROVED'
        ));
      })
      .catch(() => { if (!cancelled) setTemplates([]); });
    return () => { cancelled = true; };
  }, [needsTemplates]);

  const loadHistory = useCallback(() => {
    if (!rulerId) return;
    replyRulerService.history(rulerId)
      .then((d) => setHistory(d?.data || null))
      .catch(() => setHistory(null));
  }, [rulerId]);

  useEffect(() => { if (showHistory) loadHistory(); }, [showHistory, loadHistory]);

  const updateStep = (i, patch) => {
    setSteps((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  };

  const errors = useMemo(() => {
    const e = [];
    if (!enabled) return e;
    if (!steps.length) e.push('Adicione ao menos um degrau.');
    let last = 0;
    steps.forEach((s, i) => {
      if (s.after_minutes <= last) e.push(`Degrau ${i + 1}: o tempo tem que ser maior que o anterior.`);
      last = s.after_minutes;
      if (s.action === 'message' && !(s.text || '').trim()) e.push(`Degrau ${i + 1}: escreva a mensagem.`);
      if (s.action === 'media' && !(s.files || []).length) e.push(`Degrau ${i + 1}: anexe o arquivo.`);
      if (s.action === 'agent' && !(s.instruction || '').trim()) e.push(`Degrau ${i + 1}: escreva a instrução.`);
      if (s.action === 'template' && !s.template_name) e.push(`Degrau ${i + 1}: escolha o template.`);
      if (s.action === 'template' && !supports(channel, 'template')) e.push(`Degrau ${i + 1}: template só existe no ${whereSupported('template')}.`);
      if (templateUi && s.action === 'template' && s.template_name) {
        const tpl = findTemplate(uniqueApproved(templates), s.template_name, s.template_language);
        const faltas = tpl ? missingValues(analyzeTemplate(tpl), valuesFromMessage(s), trailCaps) : [];
        if (faltas.length) e.push(`Degrau ${i + 1}: ${faltas[0]}`);
      }
    });
    if (templateUi && channel === 'wa_business' && fallbackTemplate) {
      const tpl = findTemplate(uniqueApproved(templates), fallbackTemplate, fallbackLanguage);
      const uso = tpl ? templateUsability(analyzeTemplate(tpl), fallbackCaps) : null;
      if (uso && !uso.usable) e.push(`Template fora da janela de 24 horas: ${uso.reason}`);
    }
    return e;
  }, [enabled, steps, channel, templateUi, templates, trailCaps, fallbackTemplate, fallbackLanguage, fallbackCaps]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: `Sequência: ${label?.name || 'etapa'}`,
        enabled,
        channel,
        pipeline_id: pipelineId,
        label_ids: [labelId],
        anchor: 'stage_entry',
        validate_with_agent: validateWithAgent,
        guards,
        fallback_template_name: channel === 'wa_business' ? (fallbackTemplate || null) : null,
        fallback_template_language: channel === 'wa_business' && fallbackTemplate
          ? (templateUi ? (fallbackLanguage || 'pt_BR') : 'pt_BR') : null,
        steps: steps.map((s) => ({
          after_minutes: s.after_minutes,
          action: s.action,
          text: ['message', 'media'].includes(s.action) ? (s.text || '').trim() : null,
          instruction: s.action === 'agent' ? (s.instruction || '').trim() : null,
          template_name: s.action === 'template' ? s.template_name : null,
          template_language: s.action === 'template' ? (s.template_language || 'pt_BR') : null,
          // Com o editor novo os valores do degrau são preservados ao salvar
          // (antes o salvar os apagava).
          ...(templateUi && s.action === 'template' ? {
            template_params: s.template_params?.length ? s.template_params : null,
            ...(trailCaps.header || trailCaps.media ? { header_param: s.header_param || null } : {}),
            ...(trailCaps.buttons ? { button_params: s.button_params || null } : {}),
          } : {}),
          files: s.action === 'media'
            ? (s.files || []).map((f) => ({
              url: f.url, type: f.type || 'document',
              filename: f.filename || 'arquivo', mimeType: f.mimeType || '',
            }))
            : null,
          close: !!s.close,
          move_to_label_id: s.close ? (s.move_to_label_id || null) : null,
        })),
      };
      const res = rulerId
        ? await replyRulerService.update(rulerId, payload)
        : await replyRulerService.create(payload);
      setRulerId(res?.data?.id || rulerId);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Erro ao salvar a sequência');
    } finally {
      setSaving(false);
    }
  };

  if (!available || !labelId) return null;

  const input = {
    padding: '8px 10px', borderRadius: 8,
    border: `1px solid ${colors.borderColor}`,
    backgroundColor: colors.bgPrimary, color: colors.textPrimary,
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };
  const tiny = { fontSize: 11, color: colors.textTertiary };

  return (
    <section style={stepStyles.section}>
      <label style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
        padding: 12, borderRadius: 10,
        backgroundColor: enabled ? `${colors.accentPrimary}08` : colors.bgSecondary,
        border: `1px solid ${enabled ? colors.accentPrimary : colors.borderColor}`,
        transition: 'all 0.15s',
      }}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => {
            setEnabled(e.target.checked);
            if (e.target.checked && !steps.length) setSteps([emptyStep(1440)]);
          }}
          style={{ marginTop: 3 }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <ListOrdered size={14} color={colors.accentPrimary} />
            <strong style={{ fontSize: 13, color: colors.textPrimary }}>
              Sequência de follow-up nesta etapa
            </strong>
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: colors.textSecondary }}>
            Uma série de mensagens ao longo do tempo, contada a partir do momento
            em que o contato entra aqui. Para sozinha se o contato responder, se
            alguém da equipe falar com ele, ou se ele mudar de etapa.
          </p>
        </div>
      </label>

      {enabled && (
        <div style={{ ...stepStyles.body, marginLeft: 0, marginTop: 10 }}>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
              <Loader2 size={16} color={colors.accentPrimary} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          )}

          {autoDispatchEnabled && (
            <div style={{
              display: 'flex', gap: 8, alignItems: 'flex-start', padding: 10,
              borderRadius: 8, backgroundColor: '#f59e0b12',
              border: '1px solid #f59e0b55',
            }}>
              <AlertCircle size={14} color="#f59e0b" style={{ marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: colors.textSecondary }}>
                O auto-disparo acima também manda mensagem quando o contato entra
                aqui. Com os dois ligados o contato recebe as duas coisas. Se a
                primeira mensagem da sequência já cobre o que o auto-disparo
                manda, desligue um dos dois.
              </span>
            </div>
          )}

          <div>
            <label style={{ ...tiny, fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Enviar por
            </label>
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              style={{ ...input, width: '100%' }}
            >
              {CHANNELS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            {isWindowChannel(channel) && (
              <div style={{ ...tiny, marginTop: 4 }}>
                Instagram e Messenger só permitem enviar até 24 horas depois da
                última mensagem do contato. Degrau que cair fora disso é pulado e
                fica registrado no histórico.
              </div>
            )}
          </div>

          {/* Degraus */}
          {steps.map((step, i) => {
            const disp = toDisplay(step.after_minutes);
            return (
              <div key={i} style={{
                border: `1px solid ${colors.borderColor}`, borderRadius: 10, padding: 12,
                display: 'flex', flexDirection: 'column', gap: 8,
                backgroundColor: colors.bgPrimary,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ ...stepStyles.num, width: 20, height: 20, fontSize: 11 }}>{i + 1}</span>
                  <strong style={{ fontSize: 13, color: colors.textPrimary, flex: 1 }}>
                    Degrau {i + 1}
                  </strong>
                  <button
                    onClick={() => setSteps((p) => p.filter((_, j) => j !== i))}
                    title="Remover degrau"
                    style={{ background: 'none', border: 'none', color: colors.textTertiary, cursor: 'pointer' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: colors.textSecondary }}>Depois de</span>
                  <input
                    type="number" min={1} value={disp.value}
                    onChange={(e) => updateStep(i, { after_minutes: toMinutes(e.target.value, disp.unit) })}
                    style={{ ...input, width: 70 }}
                  />
                  <select
                    value={disp.unit}
                    onChange={(e) => updateStep(i, { after_minutes: toMinutes(disp.value, e.target.value) })}
                    style={input}
                  >
                    <option value="minutes">minutos</option>
                    <option value="hours">horas</option>
                    <option value="days">dias</option>
                  </select>
                  <span style={{ fontSize: 12, color: colors.textSecondary }}>na etapa</span>
                </div>

                <select
                  value={step.action}
                  onChange={(e) => updateStep(i, { action: e.target.value })}
                  style={{ ...input, width: '100%' }}
                >
                  {ACTIONS
                    .filter((a) => templateUi || a.value !== 'template' || supports(channel, 'template'))
                    .map((a) => {
                      // Item de tela compartilhada que o canal nao tem fica
                      // DESABILITADO com o motivo (mapa de capacidades).
                      const soBusiness = a.value === 'template' && !supports(channel, 'template');
                      return (
                        <option key={a.value} value={a.value} disabled={soBusiness}>
                          {a.label}{soBusiness ? `: só no ${whereSupported('template')}` : ''}
                        </option>
                      );
                    })}
                </select>

                {step.action === 'message' && (
                  <textarea
                    value={step.text || ''}
                    onChange={(e) => updateStep(i, { text: e.target.value })}
                    placeholder="Ex.: {{primeiro_nome}}, ainda tem interesse?"
                    maxLength={4096}
                    style={{ ...input, width: '100%', minHeight: 64, resize: 'vertical' }}
                  />
                )}

                {step.action === 'agent' && (
                  <>
                    <textarea
                      value={step.instruction || ''}
                      onChange={(e) => updateStep(i, { instruction: e.target.value })}
                      placeholder="Ex.: retome a conversa com leveza; se o cliente já se despediu, não insista."
                      maxLength={2000}
                      style={{ ...input, width: '100%', minHeight: 64, resize: 'vertical' }}
                    />
                    <span style={tiny}>
                      Quem escreve é o agente responsável pelo contato. Ele lê a
                      conversa e pode decidir não enviar nada.
                    </span>
                  </>
                )}

                {step.action === 'template' && templateUi && (
                  <TemplateMessageField
                    templates={templates}
                    templateName={step.template_name || ''}
                    templateLanguage={step.template_language || ''}
                    onTemplateChange={(tpl) => updateStep(i, {
                      template_name: tpl?.name || '',
                      template_language: tpl?.language || 'pt_BR',
                    })}
                    values={valuesFromMessage(step)}
                    onValuesChange={(vals) => updateStep(i, {
                      template_params: vals.body_params,
                      ...(trailCaps.header || trailCaps.media ? { header_param: vals.header_param } : {}),
                      ...(trailCaps.buttons ? { button_params: vals.button_params } : {}),
                    })}
                    capabilities={trailCaps}
                    colors={colors}
                  />
                )}
                {step.action === 'template' && !templateUi && (
                  <select
                    value={step.template_name || ''}
                    onChange={(e) => updateStep(i, { template_name: e.target.value })}
                    style={{ ...input, width: '100%' }}
                  >
                    <option value="">
                      {templates.length ? 'Selecione um template...' : 'Nenhum template aprovado'}
                    </option>
                    {templates.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
                  </select>
                )}

                {step.action === 'media' && (
                  <>
                    {(step.files || []).map((f, fi) => (
                      <div key={`${f.url}-${fi}`} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '6px 10px', borderRadius: 6,
                        border: `1px solid ${colors.borderColor}`,
                        backgroundColor: colors.bgSecondary,
                      }}>
                        <Paperclip size={13} color={colors.textSecondary} />
                        <span style={{
                          flex: 1, fontSize: 12, color: colors.textPrimary,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{f.filename || 'arquivo'}</span>
                        <button
                          onClick={() => updateStep(i, { files: (step.files || []).filter((_, x) => x !== fi) })}
                          style={{ background: 'none', border: 'none', color: colors.textTertiary, cursor: 'pointer' }}
                        ><Trash2 size={13} /></button>
                      </div>
                    ))}
                    {(step.files || []).length < MAX_STEP_FILES && (
                      <label style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '6px 12px', borderRadius: 6, fontSize: 12,
                        border: `1px dashed ${colors.borderColor}`,
                        color: colors.textSecondary,
                        cursor: uploadingStep === i ? 'wait' : 'pointer',
                      }}>
                        {uploadingStep === i
                          ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                          : <Paperclip size={13} />}
                        {uploadingStep === i ? 'Enviando...' : 'Anexar arquivo'}
                        <input
                          type="file"
                          accept={fileUploadService.getAcceptString()}
                          disabled={uploadingStep !== null}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            e.target.value = '';
                            if (!file) return;
                            setUploadingStep(i);
                            setError('');
                            try {
                              const up = await fileUploadService.uploadFile(file, null, { channel });
                              setSteps((prev) => prev.map((s, j) => (j === i
                                ? { ...s, files: [...(s.files || []), up] } : s)));
                            } catch (err) {
                              setError(err?.message || 'Erro ao anexar o arquivo');
                            } finally {
                              setUploadingStep(null);
                            }
                          }}
                          style={{ display: 'none' }}
                        />
                      </label>
                    )}
                    <input
                      value={step.text || ''}
                      onChange={(e) => updateStep(i, { text: e.target.value })}
                      placeholder="Legenda do arquivo (opcional)"
                      maxLength={1024}
                      style={{ ...input, width: '100%' }}
                    />
                  </>
                )}

                <label style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginTop: 2,
                  fontSize: 12, color: colors.textPrimary, cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={!!step.close}
                    onChange={(e) => updateStep(i, { close: e.target.checked })}
                  />
                  Encerrar a sequência neste degrau
                </label>
                {step.close && (
                  <select
                    value={step.move_to_label_id || ''}
                    onChange={(e) => updateStep(i, { move_to_label_id: e.target.value })}
                    style={{ ...input, width: '100%' }}
                  >
                    <option value="">Não mover de etapa</option>
                    {(funnelLabels || [])
                      .filter((l) => l.id !== labelId)
                      .map((l) => <option key={l.id} value={l.id}>Mover para: {l.name}</option>)}
                  </select>
                )}
              </div>
            );
          })}

          {steps.length < MAX_STEPS && (
            <button
              onClick={() => setSteps((p) => [
                ...p, emptyStep(Math.max(1440, (p[p.length - 1]?.after_minutes || 720) * 2)),
              ])}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
                padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                border: `1px dashed ${colors.borderColor}`,
                backgroundColor: 'transparent', color: colors.textSecondary, cursor: 'pointer',
              }}
            >
              <Plus size={14} /> Adicionar degrau
            </button>
          )}

          {/* Conferência por IA */}
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer',
            fontSize: 12, color: colors.textPrimary,
          }}>
            <input
              type="checkbox"
              checked={validateWithAgent}
              onChange={(e) => setValidateWithAgent(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>
              <ShieldCheck size={13} color={colors.accentPrimary} style={{ verticalAlign: -2, marginRight: 4 }} />
              Conferir com a IA antes de cada envio
              <span style={{ ...tiny, display: 'block', marginTop: 2 }}>
                O agente lê a conversa e segura a mensagem quando ela não faz mais
                sentido (cliente já comprou, já desistiu, pediu para não receber).
                Se a IA não responder a tempo, a mensagem é enviada.
              </span>
            </span>
          </label>

          {/* Fallback de template (Business) */}
          {channel === 'wa_business' && (
            <div>
              <label style={{ ...tiny, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                Se passar de 24 horas sem resposta, enviar este template
              </label>
              {templateUi ? (
                <TemplatePicker
                  templates={templates}
                  value={fallbackTemplate}
                  language={fallbackLanguage}
                  onChange={(tpl) => {
                    setFallbackTemplate(tpl?.name || '');
                    setFallbackLanguage(tpl?.language || 'pt_BR');
                  }}
                  capabilities={fallbackCaps}
                  colors={colors}
                  noneLabel="Não enviar nada (pular o degrau)"
                />
              ) : (
              <select
                value={fallbackTemplate}
                onChange={(e) => setFallbackTemplate(e.target.value)}
                style={{ ...input, width: '100%' }}
              >
                <option value="">Não enviar nada (pular o degrau)</option>
                {templates.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
              </select>
              )}
              <div style={{ ...tiny, marginTop: 4 }}>
                Fora da janela de 24 horas a Meta não deixa enviar mensagem livre.
                Sem um template aqui, o degrau é pulado.
              </div>
            </div>
          )}

          {/* Proteções */}
          <button
            onClick={() => setShowGuards((v) => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontSize: 12, fontWeight: 600, color: colors.accentPrimary,
            }}
          >
            {showGuards ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Proteções de envio
          </button>
          {showGuards && (
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 10, padding: 12,
              border: `1px solid ${colors.borderColor}`, borderRadius: 10,
              backgroundColor: colors.bgSecondary,
            }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <div>
                  <label style={{ ...tiny, fontWeight: 600, display: 'block' }}>
                    Não enviar se a conversa teve mensagem nos últimos (min)
                  </label>
                  <input
                    type="number" min={0} value={guards.min_silence_minutes}
                    onChange={(e) => setGuards((g) => ({ ...g, min_silence_minutes: Number(e.target.value) || 0 }))}
                    style={{ ...input, width: 90 }}
                  />
                </div>
                <div>
                  <label style={{ ...tiny, fontWeight: 600, display: 'block' }}>
                    Intervalo mínimo entre degraus (min)
                  </label>
                  <input
                    type="number" min={0} value={guards.min_interval_minutes}
                    onChange={(e) => setGuards((g) => ({ ...g, min_interval_minutes: Number(e.target.value) || 0 }))}
                    style={{ ...input, width: 90 }}
                  />
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!guards.quiet_hours?.enabled}
                  onChange={(e) => setGuards((g) => ({
                    ...g, quiet_hours: { ...g.quiet_hours, enabled: e.target.checked },
                  }))}
                />
                Não enviar de madrugada
              </label>
              {guards.quiet_hours?.enabled && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
                  <span style={{ color: colors.textSecondary }}>Silêncio das</span>
                  <input
                    type="time" value={guards.quiet_hours.start}
                    onChange={(e) => setGuards((g) => ({ ...g, quiet_hours: { ...g.quiet_hours, start: e.target.value } }))}
                    style={{ ...input, width: 110 }}
                  />
                  <span style={{ color: colors.textSecondary }}>às</span>
                  <input
                    type="time" value={guards.quiet_hours.end}
                    onChange={(e) => setGuards((g) => ({ ...g, quiet_hours: { ...g.quiet_hours, end: e.target.value } }))}
                    style={{ ...input, width: 110 }}
                  />
                </div>
              )}
              {flags.automation_business_window && (
                <BusinessWindowFields
                  compact
                  colors={colors}
                  value={guards.quiet_hours}
                  onChange={(q) => setGuards((g) => ({ ...g, quiet_hours: { ...g.quiet_hours, ...q } }))}
                />
              )}
              <span style={tiny}>
                Os valores padrão vieram do sistema anterior do cliente e já
                evitaram envio errado lá. Mexa só se souber o efeito.
              </span>
            </div>
          )}

          {/* Histórico */}
          {rulerId && (
            <>
              <button
                onClick={() => setShowHistory((v) => !v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, color: colors.accentPrimary,
                }}
              >
                {showHistory ? <ChevronUp size={14} /> : <History size={14} />} Acompanhamento
              </button>
              {showHistory && (
                <div style={{
                  padding: 12, borderRadius: 10, backgroundColor: colors.bgSecondary,
                  border: `1px solid ${colors.borderColor}`,
                  display: 'flex', flexDirection: 'column', gap: 10,
                }}>
                  {!history && <span style={tiny}>Carregando...</span>}
                  {history && (
                    <>
                      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12 }}>
                        <span style={{ color: colors.textPrimary }}>
                          <strong>{history.resumo.ativos}</strong> em andamento
                        </span>
                        <span style={{ color: colors.textSecondary }}>
                          <strong>{history.resumo.enviados}</strong> mensagens enviadas
                        </span>
                        <span style={{ color: colors.textSecondary }}>
                          <strong>{history.resumo.responderam}</strong> responderam
                        </span>
                        <span style={{ color: colors.textSecondary }}>
                          <strong>{history.resumo.concluidos}</strong> concluíram
                        </span>
                        {history.resumo.bloqueados_ia > 0 && (
                          <span style={{ color: colors.textSecondary }}>
                            <strong>{history.resumo.bloqueados_ia}</strong> segurados pela IA
                          </span>
                        )}
                        {history.resumo.falharam > 0 && (
                          <span style={{ color: '#ef4444' }}>
                            <strong>{history.resumo.falharam}</strong> falharam
                          </span>
                        )}
                      </div>
                      {!history.contatos.length && (
                        <span style={tiny}>Nenhum contato passou por esta sequência ainda.</span>
                      )}
                      {history.contatos.slice(0, 20).map((c) => (
                        <div key={c.contato} style={{
                          borderTop: `1px solid ${colors.borderColor}`, paddingTop: 8,
                        }}>
                          <div style={{ fontSize: 12, color: colors.textPrimary, fontWeight: 600 }}>
                            {c.contato}
                            <span style={{ ...tiny, fontWeight: 400, marginLeft: 6 }}>
                              {c.encerrado_por
                                ? `encerrado: ${c.encerrado_por}`
                                : c.segurado_por
                                  ? `aguardando: ${c.segurado_por}`
                                  : `degrau ${c.degrau_atual}`}
                            </span>
                          </div>
                          {c.linha_do_tempo.map((l, li) => (
                            <div key={li} style={{ ...tiny, marginTop: 2 }}>
                              {(l.em || '').slice(0, 16).replace('T', ' ')} · {l.texto}
                            </div>
                          ))}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {errors.length > 0 && (
            <div style={{
              display: 'flex', gap: 8, alignItems: 'flex-start', padding: 10,
              borderRadius: 8, backgroundColor: '#ef444412',
              border: '1px solid #ef444455',
            }}>
              <AlertCircle size={14} color="#ef4444" style={{ marginTop: 2, flexShrink: 0 }} />
              <div style={{ fontSize: 12, color: colors.textSecondary }}>
                {errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            </div>
          )}
          {error && (
            <div style={{ fontSize: 12, color: '#ef4444' }}>{error}</div>
          )}

          <button
            onClick={handleSave}
            disabled={saving || errors.length > 0}
            style={{
              alignSelf: 'flex-start', padding: '8px 18px', borderRadius: 8,
              fontSize: 13, fontWeight: 600, border: 'none', cursor: (saving || errors.length) ? 'not-allowed' : 'pointer',
              backgroundColor: (saving || errors.length) ? colors.borderColor : colors.accentPrimary,
              color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              : savedFlash ? <Check size={14} /> : <Timer size={14} />}
            {saving ? 'Salvando...' : savedFlash ? 'Salvo' : 'Salvar sequência'}
          </button>
        </div>
      )}
    </section>
  );
};

export default StageTrailSection;
